<template>
  <div class="dash" :class="{ 'dash--nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <main class="dash__main">
      <div class="dash__inner">
        <header class="dash__toolbar">
          <div class="dash__toolbar-start">
            <p class="dash__eyebrow">{{ formattedCurrentDate }}</p>
            <h1 class="dash__title">
              {{ greetingText }}<span v-if="userDisplayName">, {{ userDisplayName }}</span>
            </h1>
          </div>
          <div class="dash__toolbar-end">
            <div class="dash__period" role="group" aria-label="Reporting month">
              <button type="button" class="dash__period-btn" @click="previousPeriod" aria-label="Previous month">
                <i class="bx bx-chevron-left"></i>
              </button>
              <span class="dash__period-label">{{ dashboardMonthLabelLong }}</span>
              <button type="button" class="dash__period-btn" @click="nextPeriod" :disabled="!canGoNextMonth" aria-label="Next month">
                <i class="bx bx-chevron-right"></i>
              </button>
            </div>
            <span class="dash__updated" v-if="lastUpdatedLabel">{{ lastUpdatedLabel }}</span>
            <button type="button" class="dash__refresh" @click="refreshDashboard" :disabled="refreshing" aria-label="Refresh dashboard">
              <i class="bx bx-refresh" :class="{ 'dash__spin': refreshing }"></i>
            </button>
          </div>
        </header>

        <section class="dash__kpis" :class="{ 'dash__kpis--ready': chartsReady }" aria-label="Key metrics">
          <article class="kpi kpi--establishment" :style="panelDelay(0)" tabindex="0" @click="goToEmployeeRegister" @keyup.enter="goToEmployeeRegister">
            <div class="kpi__icon"><i class="bx bx-group"></i></div>
            <div class="kpi__body">
              <span class="kpi__label">Establishment</span>
              <span class="kpi__value">{{ formatNumber(metrics.totalEmployees) }}</span>
              <span class="kpi__meta kpi__meta--up" v-if="metrics.newEmployeeRate > 0">
                <i class="bx bx-trending-up"></i>{{ metrics.newEmployeeRate }}% growth
              </span>
              <span class="kpi__meta" v-else>Stable headcount</span>
            </div>
          </article>

          <article class="kpi kpi--hires" :style="panelDelay(1)" tabindex="0" @click="goToHRActions" @keyup.enter="goToHRActions">
            <div class="kpi__icon"><i class="bx bx-user-plus"></i></div>
            <div class="kpi__body">
              <span class="kpi__label">New this month</span>
              <span class="kpi__value">{{ formatNumber(metrics.employeesAddedThisMonth) }}</span>
              <span class="kpi__meta kpi__meta--accent">Hires</span>
            </div>
          </article>

          <article class="kpi kpi--risk" :style="panelDelay(2)" tabindex="0" @click="goToAttendance" @keyup.enter="goToAttendance">
            <div class="kpi__icon"><i class="bx bx-user-x"></i></div>
            <div class="kpi__body">
              <span class="kpi__label">Unauthorized absence</span>
              <span class="kpi__value">{{ formatNumber(metrics.absentWithoutPermission) }}</span>
              <span class="kpi__meta kpi__meta--down">{{ metrics.absentWithoutPermissionRate }}% of staff</span>
            </div>
          </article>

          <article class="kpi kpi--leave" :style="panelDelay(3)" tabindex="0" @click="goToLeaveManagement" @keyup.enter="goToLeaveManagement">
            <div class="kpi__icon"><i class="bx bx-calendar-check"></i></div>
            <div class="kpi__body">
              <span class="kpi__label">Approved leave</span>
              <span class="kpi__value">{{ formatNumber(metrics.absentWithPermission) }}</span>
              <span class="kpi__meta">{{ metrics.absentWithPermissionRate }}% of staff</span>
            </div>
          </article>
        </section>

        <div class="dash__board" :class="{ 'dash__board--ready': chartsReady }">

          <section class="dash-section dash-section--attendance" aria-labelledby="sec-attendance">
            <header class="dash-section__head">
              <div class="dash-section__lead">
                <span class="dash-section__icon" aria-hidden="true"><i class="bx bx-time-five"></i></span>
                <div class="dash-section__titles">
                  <h2 id="sec-attendance" class="dash-section__title">Attendance</h2>
                  <p class="dash-section__sub">Who clocked in each day · {{ attendanceRangeLabel }}</p>
                </div>
              </div>
            </header>

            <div class="attendance-overview" :style="panelDelay(4)">
              <button
                type="button"
                class="attendance-stat attendance-stat--in attendance-stat--button"
                @click="openClockedInToday"
              >
                <span class="attendance-stat__label">Clocked in today</span>
                <span class="attendance-stat__value">{{ todayAttendanceStats.present || 0 }}</span>
                <span class="attendance-stat__hint">of {{ formatNumber(attendanceStaffCount) }} active</span>
              </button>
              <button
                type="button"
                class="attendance-stat attendance-stat--out attendance-stat--button"
                @click="openTodayAttendanceList('absent')"
              >
                <span class="attendance-stat__label">Not clocked in</span>
                <span class="attendance-stat__value">{{ todayAttendanceStats.absent || 0 }}</span>
                <span class="attendance-stat__hint">excluding approved leave</span>
              </button>
              <button
                type="button"
                class="attendance-stat attendance-stat--leave attendance-stat--button"
                @click="openTodayAttendanceList('leave')"
              >
                <span class="attendance-stat__label">On leave today</span>
                <span class="attendance-stat__value">{{ todayAttendanceStats.onLeave || 0 }}</span>
              </button>
              <div class="attendance-stat attendance-stat--month">
                <span class="attendance-stat__label">Month present rate</span>
                <span class="attendance-stat__value">{{ formatPct(attendanceOrgPct) }}<em>%</em></span>
                <span class="attendance-stat__hint">{{ attendanceWorkingDaysCount }} working days</span>
              </div>
            </div>

            <div class="dash-grid">
              <article class="card card--span-9 attendance-chart-card" :style="panelDelay(5)">
                <header class="card__head card__head--split card__head--wrap">
                  <div>
                    <h3 class="card__title">Daily presence</h3>
                    <p class="card__hint">Stacked view of employees who clocked in vs did not, per day</p>
                  </div>
                  <div class="attendance-toolbar">
                    <div class="attendance-presets" role="group" aria-label="Quick date ranges">
                      <button
                        type="button"
                        class="attendance-presets__btn"
                        :class="{ 'attendance-presets__btn--active': attendancePreset === '7d' }"
                        @click="setAttendancePreset('7d')"
                      >7 days</button>
                      <button
                        type="button"
                        class="attendance-presets__btn"
                        :class="{ 'attendance-presets__btn--active': attendancePreset === '14d' }"
                        @click="setAttendancePreset('14d')"
                      >14 days</button>
                      <button
                        type="button"
                        class="attendance-presets__btn"
                        :class="{ 'attendance-presets__btn--active': attendancePreset === 'month' }"
                        @click="setAttendancePreset('month')"
                      >This month</button>
                    </div>
                    <div class="attendance-range" role="group" aria-label="Custom date range">
                      <input
                        v-model="attendanceRangeStart"
                        type="date"
                        class="attendance-range__input"
                        :max="attendanceRangeEnd || todayYmd"
                        aria-label="Start date"
                      />
                      <span class="attendance-range__sep">–</span>
                      <input
                        v-model="attendanceRangeEnd"
                        type="date"
                        class="attendance-range__input"
                        :min="attendanceRangeStart"
                        :max="todayYmd"
                        aria-label="End date"
                      />
                      <button type="button" class="attendance-range__apply" @click="applyAttendanceRange">Apply</button>
                    </div>
                    <button type="button" class="card__action" @click="exportChart('dailyAttendance')" aria-label="Export chart">
                      <i class="bx bx-download"></i>
                    </button>
                  </div>
                </header>
                <div class="card__body card__body--chart">
                  <div v-if="dailyAttendanceLoading" class="card__empty card__empty--chart">
                    <div class="dash__loader dash__loader--sm"></div>
                    <span>Loading attendance…</span>
                  </div>
                  <template v-else-if="dailyAttendanceData?.days?.length">
                    <div ref="dailyAttendanceChart" id="DailyAttendanceContainer" class="card__chart card__chart--hero"></div>
                  </template>
                  <div v-else class="card__empty card__empty--chart">
                    <i class="bx bx-bar-chart-alt-2"></i>
                    <p>No attendance data for this range</p>
                    <button type="button" class="attendance-range__apply" @click="setAttendancePreset('month')">Reset to this month</button>
                  </div>
                </div>
              </article>

              <article class="card card--span-3 today-card" :style="panelDelay(6)">
                <header class="card__head">
                  <h3 class="card__title">Today</h3>
                  <button type="button" class="card__action" @click="refreshTodayAttendance" aria-label="Refresh today">
                    <i class="bx bx-refresh"></i>
                  </button>
                </header>
                <div class="card__body card__body--today">
                  <div v-if="!todayAttendanceData" class="card__empty"><div class="dash__loader dash__loader--sm"></div></div>
                  <template v-else>
                    <div ref="todayAttendanceChart" id="TodayAttendanceContainer" class="card__chart card__chart--donut"></div>
                    <ul class="today-breakdown">
                      <li class="today-breakdown__row today-breakdown__row--clickable" @click="openClockedInToday">
                        <span class="today-breakdown__dot today-breakdown__dot--in"></span>
                        <span class="today-breakdown__label">Present</span>
                        <strong>{{ todayAttendanceStats.present || 0 }}</strong>
                      </li>
                      <li class="today-breakdown__row today-breakdown__row--clickable" @click="openTodayAttendanceList('absent')">
                        <span class="today-breakdown__dot today-breakdown__dot--out"></span>
                        <span class="today-breakdown__label">Absent</span>
                        <strong>{{ todayAttendanceStats.absent || 0 }}</strong>
                      </li>
                      <li class="today-breakdown__row today-breakdown__row--clickable" @click="openTodayAttendanceList('leave')">
                        <span class="today-breakdown__dot today-breakdown__dot--leave"></span>
                        <span class="today-breakdown__label">On leave</span>
                        <strong>{{ todayAttendanceStats.onLeave || 0 }}</strong>
                      </li>
                    </ul>
                  </template>
                </div>
              </article>

              <article
                v-if="deptAttendanceMonthData?.departments?.length"
                class="card card--span-12"
                :style="panelDelay(7)"
              >
                <header class="card__head card__head--split">
                  <div>
                    <h3 class="card__title">Attendance by department</h3>
                    <p class="card__hint">{{ deptAttendanceMonthData.subtitle || dashboardMonthLabelLong }}</p>
                  </div>
                  <button type="button" class="card__action" @click="exportChart('deptAttendance')" aria-label="Export department chart">
                    <i class="bx bx-download"></i>
                  </button>
                </header>
                <div class="card__body card__body--chart">
                  <div ref="deptAttendanceChart" class="card__chart card__chart--dept"></div>
                </div>
              </article>
            </div>
          </section>

          <section class="dash-section" aria-labelledby="sec-movement">
            <header class="dash-section__head">
              <div class="dash-section__lead">
                <span class="dash-section__icon dash-section__icon--movement" aria-hidden="true"><i class="bx bx-transfer-alt"></i></span>
                <div class="dash-section__titles">
                  <h2 id="sec-movement" class="dash-section__title">Workforce movement</h2>
                  <p class="dash-section__sub">Hires, exits, and separation patterns · {{ dashboardMonthLabelLong }}</p>
                </div>
              </div>
            </header>

            <div class="dash-grid">
              <article class="card card--span-12 turnover-hero" :style="panelDelay(7)">
                <header class="card__head card__head--split">
                  <div>
                    <h3 class="card__title">Staff turnover by department</h3>
                    <p class="card__hint" v-if="staffTurnoverData">{{ turnoverChartSubtitle }}</p>
                  </div>
                  <div class="card__head-actions">
                    <button type="button" class="card__action" @click="exportChart('turnover')" :disabled="!staffTurnoverData" aria-label="Export chart"><i class="bx bx-image"></i></button>
                    <button type="button" class="card__action" @click="exportTurnoverCsv" :disabled="!turnoverMovementRows.length" aria-label="Export CSV"><i class="bx bx-download"></i></button>
                  </div>
                </header>
                <div class="card__body turnover-hero__body">
                  <div v-if="!staffTurnoverData" class="card__empty"><div class="dash__loader dash__loader--sm"></div></div>
                  <template v-else>
                    <div class="turnover-kpis turnover-kpis--inline">
                      <div class="turnover-kpis__item turnover-kpis__item--hire">
                        <span class="turnover-kpis__label">Total hires</span>
                        <span class="turnover-kpis__value">{{ turnoverTotals.hires }}</span>
                      </div>
                      <div class="turnover-kpis__item turnover-kpis__item--exit">
                        <span class="turnover-kpis__label">Total exits</span>
                        <span class="turnover-kpis__value">{{ turnoverTotals.exits }}</span>
                      </div>
                      <div class="turnover-kpis__item" :class="turnoverNetClass">
                        <span class="turnover-kpis__label">Net change</span>
                        <span class="turnover-kpis__value">{{ turnoverNetLabel }}</span>
                      </div>
                      <div class="turnover-kpis__item turnover-kpis__item--meta">
                        <span class="turnover-kpis__label">Departments</span>
                        <span class="turnover-kpis__value">{{ turnoverDeptCount }}</span>
                      </div>
                    </div>
                    <div v-if="!turnoverChartCategories.length" class="card__empty">
                      <i class="bx bx-buildings"></i>
                      <p>No departments found in employee records.</p>
                    </div>
                    <div v-else ref="turnoverChart" id="TurnoverDeptChart" class="card__chart card__chart--xl"></div>
                  </template>
                </div>
              </article>

              <article class="card card--span-7" :style="panelDelay(8)">
                <header class="card__head">
                  <h3 class="card__title">Per-department breakdown</h3>
                  <span class="card__badge" v-if="turnoverMovementRows.length">{{ turnoverMovementRows.length }} depts</span>
                </header>
                <div class="card__body card__body--flush">
                  <div v-if="!staffTurnoverData" class="card__empty"><div class="dash__loader dash__loader--sm"></div></div>
                  <div v-else-if="!turnoverMovementRows.length" class="card__empty">
                    <i class="bx bx-buildings"></i>
                    <p>No departments in the system.</p>
                  </div>
                  <div v-else class="table-wrap table-wrap--tall">
                    <table class="data-table data-table--zebra">
                      <thead>
                        <tr>
                          <th scope="col">Department</th>
                          <th scope="col" class="data-table__num">Hires</th>
                          <th scope="col" class="data-table__num">Exits</th>
                          <th scope="col" class="data-table__num">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in turnoverMovementRows" :key="row.name" :class="{ 'data-table__row--idle': row.hires === 0 && row.exits === 0 }">
                          <td class="data-table__dept">
                            <span class="data-table__avatar">{{ deptInitials(row.name) }}</span>
                            <span class="data-table__name" :title="row.name">{{ row.name }}</span>
                          </td>
                          <td class="data-table__num">
                            <span class="data-table__chip data-table__chip--hire">{{ row.hires }}</span>
                          </td>
                          <td class="data-table__num">
                            <span class="data-table__chip data-table__chip--exit">{{ row.exits }}</span>
                          </td>
                          <td class="data-table__num">
                            <span class="data-table__net" :class="row.net > 0 ? 'data-table__net--up' : row.net < 0 ? 'data-table__net--down' : ''">
                              {{ row.net > 0 ? '+' : '' }}{{ row.net }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </article>

              <div class="card-stack card--span-5">
                <article class="card" :style="panelDelay(9)">
                  <header class="card__head"><h3 class="card__title">Exit reasons</h3></header>
                  <div class="card__body">
                    <div ref="exitChart" id="ExitChart" class="card__chart card__chart--sm"></div>
                    <div v-if="!natureOfExitData" class="card__empty"><div class="dash__loader dash__loader--sm"></div></div>
                    <ul class="card__legend" v-if="natureOfExitData && exitSummary.length">
                      <li v-for="item in exitSummary" :key="item.name"><span>{{ item.name }}</span><strong>{{ item.value }}</strong></li>
                    </ul>
                  </div>
                </article>
                <article class="card" :style="panelDelay(10)">
                  <header class="card__head"><h3 class="card__title">Entry types</h3></header>
                  <div class="card__body">
                    <div ref="entryChart" id="EntryChart" class="card__chart card__chart--sm"></div>
                    <div v-if="!natureOfEntryData" class="card__empty"><div class="dash__loader dash__loader--sm"></div></div>
                  </div>
                </article>
              </div>
            </div>
          </section>

          <section class="dash-section" aria-labelledby="sec-profile">
            <header class="dash-section__head">
              <div class="dash-section__lead">
                <span class="dash-section__icon dash-section__icon--profile" aria-hidden="true"><i class="bx bx-pie-chart-alt-2"></i></span>
                <div class="dash-section__titles">
                  <h2 id="sec-profile" class="dash-section__title">Workforce composition</h2>
                  <p class="dash-section__sub">Diversity and inclusion metrics across the establishment</p>
                </div>
              </div>
            </header>

            <div class="dash-grid">
              <article class="card card--span-6" :style="panelDelay(11)">
                <header class="card__head"><h3 class="card__title">Gender distribution</h3></header>
                <div class="card__body">
                  <div ref="genderChart" id="GenderContainer" class="card__chart card__chart--sm"></div>
                  <div v-if="!genderComparisonData" class="card__empty"><div class="dash__loader dash__loader--sm"></div></div>
                  <ul class="card__legend" v-if="genderComparisonData">
                    <li><span>Male</span><strong>{{ genderStats.male || 0 }}</strong></li>
                    <li><span>Female</span><strong>{{ genderStats.female || 0 }}</strong></li>
                  </ul>
                </div>
              </article>

              <article class="card card--span-6" :style="panelDelay(12)">
                <header class="card__head"><h3 class="card__title">Disability inclusion</h3></header>
                <div class="card__body">
                  <div ref="disabilityChart" id="disabilityChart" class="card__chart card__chart--sm"></div>
                  <div v-if="!disabilityDistribution" class="card__empty"><div class="dash__loader dash__loader--sm"></div></div>
                  <ul class="card__legend" v-if="disabilityDistribution">
                    <li><span>With disability</span><strong>{{ disabilityStats.withDisabilities || 0 }}</strong></li>
                    <li><span>Share of workforce</span><strong>{{ disabilityStats.percentage || 0 }}%</strong></li>
                  </ul>
                </div>
              </article>
            </div>
          </section>
        </div>

        <!--<div class="data-section" v-if="showDataTable">
          <div class="section-header">
            <h3 class="section-title">
              <i class="bx bx-table"></i> Recent Activity
            </h3>
            <button class="view-all-btn" @click="goToReports">
              View All Reports <i class="bx bx-chevron-right"></i>
            </button>
          </div>
          <div class="data-table">
            <div class="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Last Activity</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="activity in recentActivities" :key="activity.id">
                    <td class="employee-cell">
                      <div class="employee-avatar">
                        <i class="bx bx-user"></i>
                      </div>
                      <div class="employee-info">
                        <span class="employee-name">{{ activity.name }}</span>
                        <span class="employee-id">{{ activity.employeeId }}</span>
                      </div>
                    </td>
                    <td>{{ activity.department }}</td>
                    <td>
                      <span class="status-badge" :class="activity.status">{{ activity.status }}</span>
                    </td>
                    <td>{{ formatDate(activity.lastActivity) }}</td>
                    <td>
                      <button class="action-btn" @click="viewEmployee(activity.id)">
                        <i class="bx bx-show"></i> View
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>-->

        <footer class="dash__footer">
          <span>{{ copyrightYear }} StaffSync</span>
          <span>Craft Silicon</span>
        </footer>
      </div>
    </main>

    <Teleport to="body">
      <div
        v-if="attendanceListModal.open"
        class="attendance-modal-overlay"
        @click.self="closeTodayAttendanceList"
      >
        <div class="attendance-modal-card">
          <div class="attendance-modal-head">
            <div>
              <h3 class="attendance-modal-title">{{ attendanceListModal.title }}</h3>
              <p class="attendance-modal-subtitle">{{ todayYmd }}</p>
            </div>
            <div style="display:flex;align-items:center;gap:10px">
              <button
                v-if="attendanceListModal.type === 'absent' && attendanceListModal.rows.length"
                type="button"
                class="attendance-reminder-btn attendance-reminder-btn--all"
                :disabled="attendanceListModal.sendingAll"
                @click="sendAllClockInReminders"
              >
                <i class="bx" :class="attendanceListModal.sendingAll ? 'bx-loader-alt bx-spin' : 'bx-bell'"></i>
                {{ attendanceListModal.sendingAll ? 'Sending...' : 'Remind All' }}
              </button>
              <button type="button" class="attendance-modal-close" @click="closeTodayAttendanceList" aria-label="Close">
                <i class="bx bx-x"></i>
              </button>
            </div>
          </div>

          <div v-if="attendanceListModal.loading" class="attendance-modal-empty">
            <div class="dash__loader dash__loader--sm"></div>
            <p>Loading employees...</p>
          </div>

          <div v-else-if="!attendanceListModal.rows.length" class="attendance-modal-empty">
            <i class="bx bx-user-check"></i>
            <p>{{ attendanceListModal.emptyMessage }}</p>
          </div>

          <div v-else class="attendance-modal-table-wrap">
            <table class="attendance-modal-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Contact</th>
                  <th v-if="attendanceListModal.type === 'absent'">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="employee in attendanceListModal.rows" :key="employee.id || employee.empNo || employee.nationalId">
                  <td>
                    <div class="attendance-modal-employee">
                      <strong>{{ employee.name || 'Unknown employee' }}</strong>
                      <span>{{ employee.empNo || employee.nationalId || '—' }}</span>
                    </div>
                  </td>
                  <td>{{ employee.departmentName || '—' }}</td>
                  <td>
                    <div class="attendance-modal-contact">
                      <span>{{ employee.email || '—' }}</span>
                      <small v-if="employee.phone">{{ employee.phone }}</small>
                    </div>
                  </td>
                  <td v-if="attendanceListModal.type === 'absent'">
                    <button
                      v-if="attendanceListModal.remindersSent[employee.id || employee.empNo || employee.nationalId]"
                      type="button"
                      class="attendance-reminder-btn attendance-reminder-btn--sent"
                      disabled
                    >
                      <i class="bx bx-check"></i> Sent
                    </button>
                    <button
                      v-else
                      type="button"
                      class="attendance-reminder-btn"
                      :disabled="!employee.email || attendanceListModal.sendingReminders[employee.id || employee.empNo || employee.nationalId]"
                      @click="sendClockInReminder(employee)"
                    >
                      <i class="bx" :class="attendanceListModal.sendingReminders[employee.id || employee.empNo || employee.nationalId] ? 'bx-loader-alt bx-spin' : 'bx-envelope'"></i>
                      {{ attendanceListModal.sendingReminders[employee.id || employee.empNo || employee.nationalId] ? 'Sending...' : 'Remind' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import Highcharts from 'highcharts';

// Load Highcharts modules
import 'highcharts/modules/drilldown';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/accessibility';
import { authUrl, execute, toast, parseApiErrorMessage } from "@/api";

const CHART_ANIM = { duration: 900, easing: 'easeOutQuart' };

Highcharts.setOptions({
  credits: {
    enabled: false
  },
  exporting: {
    buttons: {
      contextButton: {
        enabled: false
      }
    },
    chartOptions: {
      credits: {
        enabled: false
      }
    }
  },
  lang: {
    thousandsSep: ',',
    decimalPoint: '.'
  },
  chart: {
    backgroundColor: 'transparent',
    animation: CHART_ANIM,
    style: { fontFamily: 'inherit' }
  },
  plotOptions: {
    series: {
      animation: CHART_ANIM
    },
    column: {
      animation: CHART_ANIM,
      borderRadius: 6
    },
    bar: {
      animation: CHART_ANIM,
      borderRadius: 6
    },
    pie: {
      animation: CHART_ANIM
    }
  },
  colors: ['#0f766e', '#0284c7', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#65a30d', '#ea580c']
});

export default {
  name: "Dashboard",
  components: { Navigation },
  data() {
    return {
      isNavCollapsed: false,
      refreshing: false,
      chartsReady: false,
      
      // Time data — selected month drives attendance summary + department charts + turnover period
      selectedMonthAnchor: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      formattedCurrentDate: new Date().toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      
      // Metrics data
      metrics: {
        totalEmployees: 0,
        employeesAddedThisMonth: 0,
        absentWithoutPermission: 0,
        absentWithPermission: 0,
        absentWithoutPermissionRate: 0,
        absentWithPermissionRate: 0,
        newEmployeeRate: 0,
        attendanceRate: 0,
        turnoverRate: 0
      },
      
      // Attendance date range (daily clock-in breakdown)
      attendanceRangeStart: '',
      attendanceRangeEnd: '',
      dailyAttendanceData: null,
      dailyAttendanceLoading: false,
      attendancePreset: 'month',

      // Chart data
      attendanceData: null,
      deptAttendanceMonthData: null,
      todayAttendanceData: null,
      staffTurnoverData: null,
      natureOfExitData: null,
      natureOfEntryData: null,
      genderComparisonData: null,
      disabilityDistribution: null,
      
      // Computed stats
      todayAttendanceStats: {
        present: 0,
        absent: 0,
        onLeave: 0,
        late: 0,
        total: 0
      },
      genderStats: {
        male: 0,
        female: 0,
        other: 0
      },
      disabilityStats: {
        withDisabilities: 0,
        total: 0,
        percentage: 0
      },
      exitSummary: [],
      
      // Recent activities
      recentActivities: [],
      showDataTable: true,
      
      // System info
      systemUptime: '99.8%',
      lastUpdated: new Date(),
      
      // Chart instances
      chartInstances: {
        todayAttendance: null,
        dailyAttendance: null,
        deptAttendance: null,
        turnover: null,
        exit: null,
        entry: null,
        gender: null,
        disability: null
      },

      attendanceListModal: {
        open: false,
        loading: false,
        type: '',
        title: '',
        emptyMessage: '',
        rows: [],
        sendingReminders: {},
        remindersSent: {},
        sendingAll: false
      }
    }
  },
  computed: {
    dashboardYearMonthStr() {
      const y = this.selectedMonthAnchor.getFullYear();
      const m = String(this.selectedMonthAnchor.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    },
    dashboardMonthLabelLong() {
      return this.selectedMonthAnchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    todayYmd() {
      return this.toInputDate(new Date());
    },
    attendanceRangeLabel() {
      if (this.dailyAttendanceData?.startDate && this.dailyAttendanceData?.endDate) {
        const start = this.formatAttendanceDayDate(this.dailyAttendanceData.startDate);
        const end = this.formatAttendanceDayDate(this.dailyAttendanceData.endDate);
        return `${start} – ${end}`;
      }
      if (this.attendanceRangeStart && this.attendanceRangeEnd) {
        const start = this.formatAttendanceDayDate(this.attendanceRangeStart);
        const end = this.formatAttendanceDayDate(this.attendanceRangeEnd);
        return `${start} – ${end}`;
      }
      return this.dashboardMonthLabelLong;
    },
    canGoNextMonth() {
      const now = new Date();
      const firstCurrent = new Date(now.getFullYear(), now.getMonth(), 1);
      return this.selectedMonthAnchor < firstCurrent;
    },
    copyrightYear() {
      return new Date().getFullYear();
    },
    userDisplayName() {
      try {
        const u = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
        const n = (u.names || u.name || '').trim();
        if (!n) return '';
        return n.split(/\s+/)[0];
      } catch {
        return '';
      }
    },
    attendanceOrgPct() {
      const d = this.attendanceData;
      if (d?.presentPct != null) return Number(d.presentPct);
      const present = d?.chartData?.series?.find((s) => s.name === 'Present');
      const y = present?.data?.[0]?.y;
      return y != null ? Number(y) : null;
    },
    attendanceAbsentPct() {
      const d = this.attendanceData;
      if (d?.absentPct != null) return Number(d.absentPct);
      const absent = d?.chartData?.series?.find((s) => s.name === 'Absent');
      const y = absent?.data?.[0]?.y;
      return y != null ? Number(y) : null;
    },
    attendanceStaffCount() {
      const n = this.attendanceData?.totalEmployees;
      if (n != null) return Number(n);
      return this.metrics.totalEmployees || 0;
    },
    attendanceWorkingDaysLabel() {
      const wd = this.attendanceData?.workingDays ?? this.deptAttendanceMonthData?.workingDays;
      if (!wd) return 'Active employees';
      return `${wd} working days in period`;
    },
    attendanceWorkingDaysCount() {
      const wd = this.attendanceData?.workingDays ?? this.deptAttendanceMonthData?.workingDays;
      return wd != null ? wd : '—';
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return '';
      return `Updated ${this.lastUpdated.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    },
    attendanceDeptCount() {
      const cats = this.deptAttendanceMonthData?.xAxis?.categories;
      if (Array.isArray(cats) && cats.length) return cats.length;
      const depts = this.deptAttendanceMonthData?.departments;
      if (Array.isArray(depts)) return depts.length;
      return 0;
    },
    turnoverTotals() {
      const t = this.staffTurnoverData?.totals;
      const hires = Number(t?.hires) || 0;
      const exits = Number(t?.exits) || 0;
      const net = t?.net != null ? Number(t.net) : hires - exits;
      return { hires, exits, net };
    },
    turnoverNetLabel() {
      const n = this.turnoverTotals.net;
      if (n > 0) return `+${n}`;
      return String(n);
    },
    turnoverNetClass() {
      const n = this.turnoverTotals.net;
      if (n > 0) return 'turnover-kpis__item--net-up';
      if (n < 0) return 'turnover-kpis__item--net-down';
      return '';
    },
    turnoverChartCategories() {
      const raw = this.staffTurnoverData;
      if (!raw?.chartData?.xAxis?.categories) return [];
      return raw.chartData.xAxis.categories;
    },
    turnoverChartSubtitle() {
      const sub = this.staffTurnoverData?.chartData?.subtitle;
      if (sub) return sub;
      return `New hires and exits by department · ${this.dashboardMonthLabelLong}`;
    },
    turnoverDeptCount() {
      return this.turnoverMovementRows.length;
    },
    turnoverMovementRows() {
      const raw = this.staffTurnoverData;
      if (!raw) return [];
      const mapRow = (d) => ({
        name: d.name || '',
        hires: Number(d.hires) || 0,
        exits: Number(d.exits) || 0,
        net: d.net != null ? Number(d.net) : (Number(d.hires) || 0) - (Number(d.exits) || 0)
      });
      if (Array.isArray(raw.departments) && raw.departments.length) {
        return raw.departments.map(mapRow).sort((a, b) => a.name.localeCompare(b.name));
      }
      const cats = raw.chartData?.xAxis?.categories || [];
      const hireSeries = raw.chartData?.series?.find((s) => /hire/i.test(s.name)) || raw.chartData?.series?.[0];
      const exitSeries = raw.chartData?.series?.find((s) => /exit/i.test(s.name)) || raw.chartData?.series?.[1];
      const hires = hireSeries?.data || [];
      const exits = exitSeries?.data || [];
      return cats.map((name, i) => ({
        name,
        hires: Number(hires[i]) || 0,
        exits: Number(exits[i]) || 0,
        net: (Number(hires[i]) || 0) - (Number(exits[i]) || 0)
      })).sort((a, b) => a.name.localeCompare(b.name));
    },
    greetingText() {
      const h = new Date().getHours();
      if (h < 12) return 'Good morning';
      if (h < 17) return 'Good afternoon';
      return 'Good evening';
    },
    dashboardDataLoaded() {
      return this.attendanceData && 
             this.todayAttendanceData && 
             this.staffTurnoverData && 
             this.natureOfExitData && 
             this.natureOfEntryData && 
             this.genderComparisonData && 
             this.disabilityDistribution;
    }
  },
  mounted() {
    this.syncAttendanceRangeFromMonth();
    this.initializeDashboard();
    this.setupAutoRefresh();
  },
  beforeDestroy() {
    this.cleanupCharts();
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  },
  methods: {
    // Navigation toggle handler
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },

    panelDelay(index) {
      return { '--panel-i': index };
    },

    markChartsReady() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.chartsReady = true;
        });
      });
    },
    
    // Dashboard initialization
    initializeDashboard() {
      this.loadDashboardData();
      this.loadRecentActivities();
    },
    
    // Data loading methods
    async loadDashboardData() {
      try {
        this.refreshing = true;
        await Promise.all([
          this.getEmployeeMetrics(),
          this.getAttendanceSummary(),
          this.getTodayAttendance(),
          this.getDailyAttendanceRange(),
          this.getStaffTurnover(),
          this.getNatureOfExits(),
          this.getNatureOfEntries(),
          this.getGenderComparison(),
          this.getDisabilityDistribution()
        ]);
        this.lastUpdated = new Date();
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        this.refreshing = false;
        this.markChartsReady();
      }
    },
    
    refreshDashboard() {
      this.chartsReady = false;
      this.loadDashboardData();
    },
    
    setupAutoRefresh() {
      // Refresh data every 5 minutes
      this.autoRefreshInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.loadDashboardData();
        }
      }, 300000); // 5 minutes
    },
    
    // Navigation methods
    goToHome() {
      this.$router.push('/dashboard');
    },
    
    goToEmployeeRegister() {
      this.$router.push('/employee-register');
    },
    
    goToHRActions() {
      this.$router.push('/hr-actions');
    },
    
    goToAttendance() {
      this.$router.push('/report-monthly');
    },

    openClockedInToday() {
      this.$router.push({
        path: '/report-timesheet',
        query: {
          startDate: this.todayYmd,
          endDate: this.todayYmd
        }
      });
    },

    closeTodayAttendanceList() {
      this.attendanceListModal.open = false;
      this.attendanceListModal.loading = false;
      this.attendanceListModal.type = '';
      this.attendanceListModal.title = '';
      this.attendanceListModal.emptyMessage = '';
      this.attendanceListModal.rows = [];
      this.attendanceListModal.sendingReminders = {};
      this.attendanceListModal.remindersSent = {};
      this.attendanceListModal.sendingAll = false;
    },

    sendClockInReminder(employee) {
      const key = employee.id || employee.empNo || employee.nationalId;
      if (!employee.email) return;
      this.attendanceListModal.sendingReminders = {
        ...this.attendanceListModal.sendingReminders,
        [key]: true
      };

      const data = new FormData();
      data.append('function', 'sendClockInReminder');
      data.append('employeeId', employee.id || '');
      data.append('empNo', employee.empNo || '');
      data.append('nationalId', employee.nationalId || '');
      data.append('name', employee.name || '');
      data.append('email', employee.email);

      execute(data, authUrl)
        .then(res => {
          if (res.data?.success) {
            this.attendanceListModal.remindersSent = {
              ...this.attendanceListModal.remindersSent,
              [key]: true
            };
            toast.success(res.data?.message || 'Reminder sent successfully');
          } else {
            toast.error(res.data?.message || 'Failed to send reminder');
          }
        })
        .catch((error) => {
          toast.error(parseApiErrorMessage(error, 'Failed to send reminder. Please try again.'));
        })
        .finally(() => {
          this.attendanceListModal.sendingReminders = {
            ...this.attendanceListModal.sendingReminders,
            [key]: false
          };
        });
    },

    sendAllClockInReminders() {
      const eligible = this.attendanceListModal.rows.filter(e => {
        const key = e.id || e.empNo || e.nationalId;
        return e.email && !this.attendanceListModal.remindersSent[key];
      });
      if (!eligible.length) return;
      this.attendanceListModal.sendingAll = true;
      let completed = 0;
      eligible.forEach(emp => {
        this.sendClockInReminder(emp);
        const key = emp.id || emp.empNo || emp.nationalId;
        const check = setInterval(() => {
          if (!this.attendanceListModal.sendingReminders[key]) {
            completed++;
            clearInterval(check);
            if (completed >= eligible.length) {
              this.attendanceListModal.sendingAll = false;
            }
          }
        }, 200);
      });
    },

    openTodayAttendanceList(type) {
      const config = {
        absent: {
          title: 'Employees Not Clocked In Today',
          emptyMessage: 'Everyone active has either clocked in or is on approved leave.'
        },
        leave: {
          title: 'Employees On Leave Today',
          emptyMessage: 'No active employee is on approved leave today.'
        }
      };
      const selected = config[type];
      if (!selected) return;

      this.attendanceListModal.open = true;
      this.attendanceListModal.loading = true;
      this.attendanceListModal.type = type;
      this.attendanceListModal.title = selected.title;
      this.attendanceListModal.emptyMessage = selected.emptyMessage;
      this.attendanceListModal.rows = [];

      const data = new FormData();
      data.append('function', 'getTodayAttendanceEmployeeLists');

      execute(data, authUrl)
        .then((res) => {
          if (res.data?.success && res.data?.data) {
            const payload = res.data.data;
            this.attendanceListModal.rows = type === 'absent'
              ? (payload.absentEmployees || [])
              : (payload.onLeaveEmployees || []);
          } else {
            this.attendanceListModal.rows = [];
          }
        })
        .catch((error) => {
          console.error('Error loading today attendance employee list:', error);
          this.attendanceListModal.rows = [];
        })
        .finally(() => {
          this.attendanceListModal.loading = false;
        });
    },
    
    goToLeaveManagement() {
      this.$router.push('/attendance-leave');
    },
    
    goToReports() {
      this.$router.push('/reports');
    },
    
    goToSettings() {
      this.$router.push('/settings');
    },
    
    viewEmployee(employeeId) {
      this.$router.push(`/employee-profile/${employeeId}`);
    },
    
    // Date and period methods
    previousPeriod() {
      this.selectedMonthAnchor = new Date(
        this.selectedMonthAnchor.getFullYear(),
        this.selectedMonthAnchor.getMonth() - 1,
        1
      );
      this.loadMonthScopedCharts();
    },
    
    nextPeriod() {
      if (!this.canGoNextMonth) return;
      this.selectedMonthAnchor = new Date(
        this.selectedMonthAnchor.getFullYear(),
        this.selectedMonthAnchor.getMonth() + 1,
        1
      );
      this.loadMonthScopedCharts();
    },
    
    async loadMonthScopedCharts() {
      this.syncAttendanceRangeFromMonth();
      await Promise.all([
        this.getAttendanceSummary(),
        this.getDailyAttendanceRange(),
        this.getStaffTurnover()
      ]);
    },

    toInputDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },

    syncAttendanceRangeFromMonth() {
      const y = this.selectedMonthAnchor.getFullYear();
      const m = this.selectedMonthAnchor.getMonth();
      const start = new Date(y, m, 1);
      const endOfMonth = new Date(y, m + 1, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const end = endOfMonth > today ? today : endOfMonth;
      this.attendanceRangeStart = this.toInputDate(start);
      this.attendanceRangeEnd = this.toInputDate(end);
      this.attendancePreset = 'month';
    },

    formatAttendanceDayDate(ymd) {
      if (!ymd) return '—';
      const [y, mo, d] = String(ymd).split('-').map(Number);
      if (!y || !mo || !d) return ymd;
      return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    applyAttendanceRange() {
      if (!this.attendanceRangeStart || !this.attendanceRangeEnd) return;
      if (this.attendanceRangeStart > this.attendanceRangeEnd) return;
      this.attendancePreset = 'custom';
      this.getDailyAttendanceRange();
    },

    setAttendancePreset(preset) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const end = this.toInputDate(today);

      if (preset === 'month') {
        this.syncAttendanceRangeFromMonth();
        this.attendancePreset = 'month';
      } else {
        const days = preset === '7d' ? 6 : 13;
        const start = new Date(today);
        start.setDate(start.getDate() - days);
        this.attendanceRangeStart = this.toInputDate(start);
        this.attendanceRangeEnd = end;
        this.attendancePreset = preset;
      }
      this.getDailyAttendanceRange();
    },
    
    // Formatting methods
    formatNumber(value) {
      if (!value) return '0';
      return parseInt(value).toLocaleString();
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    // API methods
    getEmployeeMetrics() {
      const data = new FormData();
      data.append("function", "getEmployeeMetrics");
      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.metrics = res.data.data.metrics;
            }
          })
          .catch((e) => {
            console.error("Error fetching employee metrics:", e);
          });
    },
    
    getAttendanceSummary() {
      const year = this.selectedMonthAnchor.getFullYear();
      const month = this.selectedMonthAnchor.getMonth() + 1;
      const data = new FormData();
      data.append("function", "getAttendanceSummary");
      data.append("year", String(year));
      data.append("month", String(month));

      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success && res.data.data) {
              this.attendanceData = res.data.data;
              this.deptAttendanceMonthData = res.data.data.deptChartData || null;
              this.$nextTick(() => {
                this.renderDeptAttendanceChart();
              });
            } else {
              this.attendanceData = null;
              this.deptAttendanceMonthData = null;
            }
          })
          .catch((e) => {
            console.error("Error fetching attendance data:", e);
            this.attendanceData = null;
            this.deptAttendanceMonthData = null;
          });
    },
    
    getTodayAttendance() {
      const data = new FormData();
      data.append("function", "getTodayAttendance");

      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.todayAttendanceData = res.data.data;
              this.calculateTodayAttendanceStats();
              this.$nextTick(() => {
                this.renderTodayAttendanceChart();
              });
            }
          })
          .catch((e) => {
            console.error("Error fetching attendance data:", e);
          });
    },
    
    refreshTodayAttendance() {
      this.getTodayAttendance();
    },

    getDailyAttendanceRange() {
      const start = this.attendanceRangeStart;
      const end = this.attendanceRangeEnd;
      if (!start || !end || start > end) {
        return Promise.resolve();
      }

      this.dailyAttendanceLoading = true;
      const data = new FormData();
      data.append('function', 'getDailyAttendanceRange');
      data.append('startDate', start);
      data.append('endDate', end);

      return execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data) {
            this.dailyAttendanceData = res.data.data;
            this.$nextTick(() => {
              requestAnimationFrame(() => {
                this.renderDailyAttendanceChart();
              });
            });
          } else {
            this.dailyAttendanceData = null;
          }
        })
        .catch((e) => {
          console.error('Error fetching daily attendance:', e);
          this.dailyAttendanceData = null;
        })
        .finally(() => {
          this.dailyAttendanceLoading = false;
        });
    },
    
    calculateTodayAttendanceStats() {
      const summary = this.todayAttendanceData?.summary;
      if (summary) {
        this.todayAttendanceStats = {
          present: summary.present || 0,
          absent: summary.absent || 0,
          onLeave: summary.onLeave || 0,
          late: 0,
          total: (summary.present || 0) + (summary.absent || 0) + (summary.onLeave || 0)
        };
        return;
      }
      if (!this.todayAttendanceData?.chartData?.series?.[0]?.data) return;

      const data = this.todayAttendanceData.chartData.series[0].data;
      this.todayAttendanceStats = {
        present: data.find((d) => d.name === 'Present')?.y || 0,
        absent: data.find((d) => d.name === 'Absent')?.y || 0,
        onLeave: data.find((d) => d.name === 'On Leave')?.y || 0,
        late: data.find((d) => d.name === 'Late')?.y || 0,
        total: data.reduce((sum, item) => sum + (item.y || 0), 0)
      };
    },
    
    getStaffTurnover() {
      const data = new FormData();
      data.append("function", "getStaffTurnover");
      data.append("selectedMonth", this.dashboardYearMonthStr);

      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.staffTurnoverData = res.data.data;
              this.$nextTick(() => {
                this.renderStaffTurnoverChart();
              });
            }
          })
          .catch((e) => {
            console.error("Error fetching staff turnover data:", e);
          });
    },
    
    getNatureOfExits() {
      const data = new FormData();
      data.append("function", "getNatureOfExits");
      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.natureOfExitData = res.data.data;
              this.calculateExitSummary();
              this.$nextTick(() => {
                this.renderNatureOfExitsChart();
              });
            }
          })
          .catch((e) => {
            console.error("Error fetching nature of exits data:", e);
          });
    },
    
    calculateExitSummary() {
      if (!this.natureOfExitData?.chartData?.series?.[0]?.data) return;
      
      this.exitSummary = this.natureOfExitData.chartData.series[0].data
        .map(item => ({
          name: item.name,
          value: item.y
        }))
        .slice(0, 5); // Show top 5
    },
    
    getNatureOfEntries() {
      const data = new FormData();
      data.append("function", "getNatureOfEntries");
      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.natureOfEntryData = res.data.data;
              this.$nextTick(() => {
                this.renderNatureOfEntriesChart();
              });
            }
          })
          .catch((e) => {
            console.error("Error fetching nature of entries data:", e);
          });
    },
    
    getGenderComparison() {
      const data = new FormData();
      data.append("function", "getGenderComparison");

      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.genderComparisonData = res.data.data;
              this.calculateGenderStats();
              this.$nextTick(() => {
                this.renderGenderComparisonChart();
              });
            }
          })
          .catch((e) => {
            console.error("Error fetching gender comparison data:", e);
          });
    },
    
    calculateGenderStats() {
      if (!this.genderComparisonData?.chartData?.series?.[0]?.data) return;
      
      this.genderComparisonData.chartData.series[0].data.forEach(item => {
        const name = item.name.toLowerCase();
        if (name.includes('male') && !name.includes('female')) {
          this.genderStats.male = item.y;
        } else if (name.includes('female')) {
          this.genderStats.female = item.y;
        } else {
          this.genderStats.other = (this.genderStats.other || 0) + item.y;
        }
      });
    },
    
    getDisabilityDistribution() {
      const data = new FormData();
      data.append("function", "getDisabilityDistribution");

      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.disabilityDistribution = res.data.data;
              this.calculateDisabilityStats();
              this.$nextTick(() => {
                this.renderDisabilityDistributionChart();
              });
            }
          })
          .catch((e) => {
            console.error("Error fetching disability distribution data:", e);
          });
    },
    
    calculateDisabilityStats() {
      if (!this.disabilityDistribution?.chartData?.series?.[0]?.data) return;
      
      const data = this.disabilityDistribution.chartData.series[0].data;
      const withDisabilities = data.find(d => d.name === 'With Disabilities')?.y || 0;
      const withoutDisabilities = data.find(d => d.name === 'Without Disabilities')?.y || 0;
      const total = withDisabilities + withoutDisabilities;
      
      this.disabilityStats = {
        withDisabilities: withDisabilities,
        total: total,
        percentage: total > 0 ? ((withDisabilities / total) * 100).toFixed(1) : 0
      };
    },
    
    loadRecentActivities() {
      // Mock data - replace with API call
      this.recentActivities = [
        {
          id: 1,
          name: 'John Doe',
          employeeId: 'EMP001',
          department: 'IT',
          status: 'present',
          lastActivity: new Date().toISOString()
        },
        {
          id: 2,
          name: 'Jane Smith',
          employeeId: 'EMP002',
          department: 'HR',
          status: 'absent',
          lastActivity: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: 3,
          name: 'Bob Johnson',
          employeeId: 'EMP003',
          department: 'Finance',
          status: 'late',
          lastActivity: new Date(Date.now() - 7200000).toISOString()
        },
        {
          id: 4,
          name: 'Alice Brown',
          employeeId: 'EMP004',
          department: 'Operations',
          status: 'present',
          lastActivity: new Date(Date.now() - 10800000).toISOString()
        },
        {
          id: 5,
          name: 'Charlie Wilson',
          employeeId: 'EMP005',
          department: 'Marketing',
          status: 'on-leave',
          lastActivity: new Date(Date.now() - 14400000).toISOString()
        }
      ];
    },
    
    // Chart rendering methods
    renderDeptAttendanceChart() {
      if (!this.$refs.deptAttendanceChart || !this.deptAttendanceMonthData) return;

      if (this.chartInstances.deptAttendance) {
        this.chartInstances.deptAttendance.destroy();
      }

      const dept = this.deptAttendanceMonthData;
      const categories = dept.xAxis?.categories || [];
      const rates = dept.series?.[0]?.data || [];

      this.chartInstances.deptAttendance = Highcharts.chart(this.$refs.deptAttendanceChart, {
        chart: { type: 'bar', height: Math.max(220, categories.length * 36) },
        title: { text: null },
        xAxis: {
          categories,
          title: { text: null },
          labels: { style: { fontSize: '11px' } }
        },
        yAxis: {
          min: 0,
          max: 100,
          title: { text: 'Attendance rate (%)' },
          labels: { format: '{value}%' }
        },
        legend: { enabled: false },
        tooltip: {
          pointFormat: '<b>{point.y:.1f}%</b> attendance rate'
        },
        plotOptions: {
          bar: {
            borderWidth: 0,
            borderRadius: 4,
            dataLabels: {
              enabled: true,
              format: '{y:.0f}%',
              style: { fontSize: '10px', fontWeight: '600', textOutline: 'none' }
            }
          }
        },
        series: [{
          name: 'Attendance rate',
          color: '#0f766e',
          data: rates
        }]
      });
    },

    renderDailyAttendanceChart() {
      if (!this.$refs.dailyAttendanceChart || !this.dailyAttendanceData?.chartData) return;

      if (this.chartInstances.dailyAttendance) {
        this.chartInstances.dailyAttendance.destroy();
      }

      const chartData = this.dailyAttendanceData.chartData;
      const days = this.dailyAttendanceData.days || [];
      const categories = days.length
        ? days.map((d) => {
            const [y, mo, day] = String(d.date).split('-').map(Number);
            return new Date(y, mo - 1, day).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            });
          })
        : (chartData.categories || []);
      const dayCount = categories.length;
      const showDataLabels = dayCount <= 16;
      const totalStaff = days[0]?.totalEmployees || this.attendanceStaffCount;

      this.chartInstances.dailyAttendance = Highcharts.chart(this.$refs.dailyAttendanceChart, {
        chart: {
          type: 'column',
          height: 380
        },
        title: { text: null },
        xAxis: {
          categories,
          crosshair: true,
          labels: {
            rotation: dayCount > 12 ? -40 : 0,
            style: { fontSize: '11px', color: '#64748b' }
          }
        },
        yAxis: {
          min: 0,
          max: totalStaff > 0 ? totalStaff : null,
          title: { text: 'Employees' },
          allowDecimals: false,
          gridLineColor: '#f1f5f9'
        },
        legend: {
          align: 'right',
          verticalAlign: 'top',
          itemStyle: { fontSize: '12px', fontWeight: '600' }
        },
        tooltip: {
          shared: true,
          useHTML: true,
          headerFormat: '<span style="font-size:12px;font-weight:700">{point.key}</span><table>',
          pointFormat:
            '<tr><td style="padding:4px 8px 4px 0;color:{series.color}">● {series.name}:</td>' +
            '<td style="font-weight:700;text-align:right">{point.y}</td></tr>',
          footerFormat: '</table>'
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            borderWidth: 0,
            borderRadius: 4,
            pointPadding: 0.08,
            groupPadding: 0.12,
            dataLabels: {
              enabled: showDataLabels,
              style: { fontSize: '10px', fontWeight: '600', textOutline: 'none', color: '#fff' }
            }
          }
        },
        series: (chartData.series || []).map((s) => ({
          name: s.name,
          color: s.color,
          data: s.data || []
        }))
      });
    },
    
    renderTodayAttendanceChart() {
      if (!this.$refs.todayAttendanceChart || !this.todayAttendanceData) return;
      
      if (this.chartInstances.todayAttendance) {
        this.chartInstances.todayAttendance.destroy();
      }
      
      const chartData = this.todayAttendanceData.chartData || {
        series: [{
          name: 'Attendance',
          data: [
            { name: 'Present', y: 75, color: '#10b981' },
            { name: 'Absent', y: 15, color: '#ef4444' },
            { name: 'Late', y: 10, color: '#f59e0b' }
          ]
        }]
      };
      
      this.chartInstances.todayAttendance = Highcharts.chart(this.$refs.todayAttendanceChart, {
        chart: {
          type: 'pie',
          height: 200
        },
        title: { text: null },
        plotOptions: {
          pie: {
            innerSize: '58%',
            allowPointSelect: false,
            cursor: 'default',
            animation: { duration: 1000, easing: 'easeOutQuart' },
            dataLabels: { enabled: false },
            showInLegend: false,
            borderWidth: 0
          },
          series: {
            animation: { duration: 1000, easing: 'easeOutQuart' }
          }
        },
        tooltip: {
          pointFormat: '{series.name}: <b>{point.y} employees</b>'
        },
        series: chartData.series
      });
    },
    
    formatPct(value) {
      if (value == null || Number.isNaN(Number(value))) return '—';
      const n = Number(value);
      return n % 1 === 0 ? String(n) : n.toFixed(1);
    },

    renderStaffTurnoverChart() {
      if (!this.$refs.turnoverChart || !this.staffTurnoverData?.chartData) return;

      if (this.chartInstances.turnover) {
        this.chartInstances.turnover.destroy();
        this.chartInstances.turnover = null;
      }

      const { chartData } = this.staffTurnoverData;
      const categories = chartData.xAxis?.categories || [];
      if (!categories.length) return;

      const series = (chartData.series || []).map((s, idx) => {
        const isExit = /exit/i.test(s.name || '');
        return {
          ...s,
          type: 'bar',
          color: isExit ? '#dc2626' : '#059669',
          data: Array.isArray(s.data) ? s.data.map((v) => Number(v) || 0) : s.data
        };
      });

      const deptCount = categories.length;
      const chartHeight = Math.min(520, Math.max(320, 56 + deptCount * 28));

      this.chartInstances.turnover = Highcharts.chart(this.$refs.turnoverChart, {
        chart: {
          type: 'bar',
          height: chartHeight
        },
        title: { text: null },
        subtitle: {
          text: chartData.subtitle || null,
          style: { color: '#64748b', fontSize: '12px' }
        },
        xAxis: {
          categories,
          title: { text: chartData.xAxis?.title?.text || 'Department' },
          labels: { style: { fontSize: '11px' } }
        },
        yAxis: {
          min: 0,
          allowDecimals: false,
          title: { text: 'Employees' },
          gridLineColor: '#f1f5f9'
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          itemStyle: { fontWeight: '600', fontSize: '12px' }
        },
        tooltip: {
          shared: true,
          valueDecimals: 0,
          pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>'
        },
        plotOptions: {
          bar: {
            grouping: true,
            groupPadding: 0.12,
            pointPadding: 0.05,
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              formatter() {
                return this.y > 0 ? Highcharts.numberFormat(this.y, 0) : '';
              },
              style: { fontSize: '10px', fontWeight: '600', textOutline: 'none' }
            }
          },
          series: { animation: CHART_ANIM }
        },
        series,
      });
    },

    exportTurnoverCsv() {
      const rows = this.turnoverMovementRows;
      if (!rows.length) return;
      const header = ['Department', 'Hires', 'Exits', 'Net'];
      const lines = rows.map((d) => [
        `"${String(d.name).replace(/"/g, '""')}"`,
        d.hires,
        d.exits,
        d.net
      ].join(','));
      const csv = [header.join(','), ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `turnover-${this.dashboardYearMonthStr}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    deptInitials(name) {
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0].slice(0, 2).toUpperCase();
    },

    renderNatureOfExitsChart() {
      if (!this.$refs.exitChart || !this.natureOfExitData) return;
      
      if (this.chartInstances.exit) {
        this.chartInstances.exit.destroy();
      }
      
      const chartData = this.natureOfExitData.chartData || {
        series: [{
          name: 'Exits',
          data: [
            { name: 'Resignation', y: 40, color: '#4f46e5' },
            { name: 'Retirement', y: 25, color: '#10b981' },
            { name: 'Termination', y: 20, color: '#ef4444' },
            { name: 'Contract End', y: 15, color: '#f59e0b' }
          ]
        }]
      };
      
      this.chartInstances.exit = Highcharts.chart(this.$refs.exitChart, {
        chart: {
          type: 'pie',
          height: 250
        },
        title: { text: null },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '{point.name}: {point.y}'
            },
            showInLegend: true
          }
        },
        series: chartData.series
      });
    },
    
    renderNatureOfEntriesChart() {
      if (!this.$refs.entryChart || !this.natureOfEntryData) return;
      
      if (this.chartInstances.entry) {
        this.chartInstances.entry.destroy();
      }
      
      const chartData = this.natureOfEntryData.chartData || {
        series: [{
          name: 'New Hires',
          data: [
            { name: 'New Hire', y: 60, color: '#4f46e5' },
            { name: 'Transfer', y: 25, color: '#10b981' },
            { name: 'Promotion', y: 15, color: '#f59e0b' }
          ]
        }]
      };
      
      this.chartInstances.entry = Highcharts.chart(this.$refs.entryChart, {
        chart: {
          type: 'pie',
          height: 250
        },
        title: { text: null },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '{point.name}: {point.y}'
            },
            showInLegend: true
          }
        },
        series: chartData.series
      });
    },
    
    renderGenderComparisonChart() {
      if (!this.$refs.genderChart || !this.genderComparisonData) return;
      
      if (this.chartInstances.gender) {
        this.chartInstances.gender.destroy();
      }
      
      const chartData = this.genderComparisonData.chartData || {
        series: [{
          name: 'Gender',
          data: [
            { name: 'Male', y: 55, color: '#3b82f6' },
            { name: 'Female', y: 45, color: '#ec4899' }
          ]
        }]
      };
      
      this.chartInstances.gender = Highcharts.chart(this.$refs.genderChart, {
        chart: {
          type: 'pie',
          height: 250
        },
        title: { text: null },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '{point.name}: {point.y}'
            },
            showInLegend: true
          }
        },
        series: chartData.series
      });
    },
    
    renderDisabilityDistributionChart() {
      if (!this.$refs.disabilityChart || !this.disabilityDistribution) return;
      
      if (this.chartInstances.disability) {
        this.chartInstances.disability.destroy();
      }
      
      const chartData = this.disabilityDistribution.chartData || {
        series: [{
          name: 'Disability',
          data: [
            { name: 'With Disabilities', y: 8, color: '#8b5cf6' },
            { name: 'Without Disabilities', y: 92, color: '#cbd5e1' }
          ]
        }]
      };
      
      this.chartInstances.disability = Highcharts.chart(this.$refs.disabilityChart, {
        chart: {
          type: 'pie',
          height: 250
        },
        title: { text: null },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '{point.percentage:.1f}%',
              distance: -30,
              style: { fontSize: '12px' }
            }
          }
        },
        series: chartData.series
      });
    },
    
    // Export methods
    exportChart(chartType) {
      const chart = this.chartInstances[chartType];
      if (chart) {
        chart.exportChart({
          type: 'image/png',
          filename: `${chartType}-chart-${new Date().toISOString().split('T')[0]}`
        });
      }
    },
    
    // Cleanup methods
    cleanupCharts() {
      Object.values(this.chartInstances).forEach(chart => {
        if (chart && chart.destroy) {
          chart.destroy();
        }
      });
    },
    
  }
}
</script>

<style scoped>
.dash {
  --dash-bg: #eef2f6;
  --dash-surface: #ffffff;
  --dash-border: #e2e8f0;
  --dash-text: #0f172a;
  --dash-muted: #64748b;
  --dash-accent: #0f766e;
  --dash-accent-soft: #ccfbf1;
  --dash-radius: 14px;
  --dash-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  --dash-shadow-lg: 0 12px 40px rgba(15, 23, 42, 0.08);
  --dash-gap: 1.25rem;
  --dash-section-gap: 2.25rem;
  min-height: 100vh;
  background: linear-gradient(165deg, #f8fafc 0%, var(--dash-bg) 45%, #e8edf3 100%);
  font-family: inherit;
  color: var(--dash-text);
}

.dash--nav-collapsed .dash__main {
  margin-left: 70px;
}

.dash__main {
  margin-left: 280px;
  padding: 1.25rem 1.5rem 2rem;
  transition: margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.dash__inner {
  max-width: 1680px;
  margin: 0 auto;
}

.dash__toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.dash__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--dash-muted);
  letter-spacing: 0.02em;
}

.dash__title {
  margin: 0;
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--dash-text);
  line-height: 1.15;
}

.dash__toolbar-end {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dash__period {
  display: flex;
  align-items: center;
  background: var(--dash-surface);
  border: 1px solid var(--dash-border);
  border-radius: 999px;
  padding: 0.2rem;
  box-shadow: var(--dash-shadow);
}

.dash__period-btn {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--dash-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s, color 0.15s;
}

.dash__period-btn:hover:not(:disabled) {
  background: var(--dash-bg);
  color: var(--dash-accent);
}

.dash__period-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.dash__period-label {
  min-width: 9rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--dash-text);
  padding: 0 0.35rem;
}

.dash__refresh {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: 1px solid var(--dash-border);
  background: var(--dash-surface);
  color: var(--dash-accent);
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: var(--dash-shadow);
  transition: transform 0.15s, border-color 0.15s;
}

.dash__refresh:hover:not(:disabled) {
  border-color: var(--dash-accent);
  transform: translateY(-1px);
}

.dash__refresh:disabled {
  opacity: 0.6;
  cursor: wait;
}

.dash__updated {
  font-size: 0.75rem;
  color: var(--dash-muted);
  white-space: nowrap;
  padding: 0 0.35rem;
}

.dash__spin {
  animation: dash-spin 0.85s linear infinite;
}

@keyframes dash-spin {
  to { transform: rotate(360deg); }
}

.dash__kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--dash-gap);
  margin-bottom: var(--dash-section-gap);
}

.kpi {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.15rem 1.2rem;
  background: var(--dash-surface);
  border: 1px solid var(--dash-border);
  border-radius: var(--dash-radius);
  box-shadow: var(--dash-shadow);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  position: relative;
  overflow: hidden;
}

.kpi::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--dash-border);
}

.kpi--establishment::before { background: #0369a1; }
.kpi--hires::before { background: #15803d; }
.kpi--risk::before { background: #dc2626; }
.kpi--leave::before { background: #d97706; }

.kpi:hover,
.kpi:focus-visible {
  transform: translateY(-2px);
  border-color: #cbd5e1;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  outline: none;
}

.kpi__icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 12px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-size: 1.35rem;
}

.kpi--establishment .kpi__icon { background: #e0f2fe; color: #0369a1; }
.kpi--hires .kpi__icon { background: #dcfce7; color: #15803d; }
.kpi--risk .kpi__icon { background: #fee2e2; color: #b91c1c; }
.kpi--leave .kpi__icon { background: #fef3c7; color: #b45309; }

.kpi__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.kpi__label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--dash-muted);
}

.kpi__value {
  margin-top: 0.35rem;
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--dash-text);
  line-height: 1;
}

.kpi__meta {
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  color: var(--dash-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.kpi__meta--up { color: #15803d; }
.kpi__meta--down { color: #b91c1c; }
.kpi__meta--accent { color: var(--dash-accent); font-weight: 600; }

.dash__kpis .kpi {
  opacity: 0;
  transform: translateY(10px);
}

.dash__kpis--ready .kpi {
  animation: dash-panel-in 0.5s ease forwards;
  animation-delay: calc(var(--panel-i, 0) * 55ms);
}

/* —— Board layout —— */
.dash__board {
  display: flex;
  flex-direction: column;
  gap: var(--dash-section-gap);
}

.dash-section__head {
  margin-bottom: 1.15rem;
}

.dash-section__lead {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.dash-section__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 1.25rem;
  flex-shrink: 0;
  background: var(--dash-accent-soft);
  color: var(--dash-accent);
}

.dash-section__icon--movement {
  background: #e0f2fe;
  color: #0369a1;
}

.dash-section__icon--profile {
  background: #ede9fe;
  color: #6d28d9;
}

.dash-section__title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--dash-text);
}

.dash-section__sub {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: var(--dash-muted);
}

.dash-section--attendance {
  padding: 1.35rem;
  background: var(--dash-surface);
  border: 1px solid var(--dash-border);
  border-radius: calc(var(--dash-radius) + 2px);
  box-shadow: var(--dash-shadow);
}

.attendance-overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.15rem;
}

.attendance-stat {
  padding: 1rem 1.1rem;
  border-radius: 12px;
  border: 1px solid var(--dash-border);
  background: #f8fafc;
}

.attendance-stat--button {
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.attendance-stat--button:hover,
.attendance-stat--button:focus-visible {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px -22px rgba(15, 23, 42, 0.55);
  border-color: rgba(99, 102, 241, 0.35);
  outline: none;
}

.attendance-stat__label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--dash-muted);
}

.attendance-stat__value {
  display: block;
  margin-top: 0.35rem;
  font-size: 1.65rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.attendance-stat__value em {
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  opacity: 0.85;
}

.attendance-stat__hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: var(--dash-muted);
}

.attendance-stat--in {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-color: #bbf7d0;
}

.attendance-stat--in .attendance-stat__value { color: #15803d; }

.attendance-stat--out {
  background: linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%);
  border-color: #fecaca;
}

.attendance-stat--out .attendance-stat__value { color: #b91c1c; }

.attendance-stat--leave {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-color: #fde68a;
}

.attendance-stat--leave .attendance-stat__value { color: #b45309; }

.attendance-stat--month {
  background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
  border-color: #99f6e4;
}

.attendance-stat--month .attendance-stat__value { color: #0f766e; }

.attendance-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
}

.attendance-presets {
  display: inline-flex;
  padding: 0.15rem;
  background: #f1f5f9;
  border-radius: 999px;
  border: 1px solid var(--dash-border);
}

.attendance-presets__btn {
  border: none;
  background: transparent;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--dash-muted);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.attendance-presets__btn:hover {
  color: var(--dash-text);
}

.attendance-presets__btn--active {
  background: var(--dash-surface);
  color: var(--dash-accent);
  box-shadow: var(--dash-shadow);
}

.attendance-range {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.15rem 0.15rem 0.15rem 0.5rem;
  background: #f8fafc;
  border: 1px solid var(--dash-border);
  border-radius: 10px;
}

.attendance-range__sep {
  color: var(--dash-muted);
  font-size: 0.75rem;
}

.attendance-range__input {
  height: 2rem;
  padding: 0 0.4rem;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: var(--dash-text);
  background: transparent;
}

.attendance-range__apply {
  height: 2rem;
  padding: 0 0.85rem;
  border: none;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 650;
  color: #fff;
  background: var(--dash-accent);
  cursor: pointer;
}

.attendance-range__apply:hover {
  filter: brightness(1.06);
}

.card__head--wrap {
  flex-wrap: wrap;
  gap: 0.75rem;
}

.card__body--chart {
  min-height: 320px;
}

.card__empty--chart {
  min-height: 320px;
}

.card__chart--hero {
  min-height: 360px;
}

.card__chart--donut {
  min-height: 200px;
}

.card__chart--dept {
  min-height: 240px;
}

.today-breakdown {
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0.75rem 0 0;
  border-top: 1px solid var(--dash-border);
}

.today-breakdown__row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0;
  font-size: 0.8125rem;
  color: var(--dash-muted);
}

.today-breakdown__row--clickable {
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;
}

.today-breakdown__row--clickable:hover,
.today-breakdown__row--clickable:focus-visible {
  background: rgba(99, 102, 241, 0.08);
  transform: translateX(2px);
}

.today-breakdown__row strong {
  font-variant-numeric: tabular-nums;
  color: var(--dash-text);
  font-weight: 700;
}

.today-breakdown__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.attendance-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.44);
  backdrop-filter: blur(2px);
}

.attendance-modal-card {
  width: min(880px, 100%);
  max-height: min(80vh, 760px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 70px -28px rgba(15, 23, 42, 0.5);
  overflow: hidden;
}

.attendance-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
}

.attendance-modal-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.attendance-modal-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.attendance-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
}

.attendance-modal-close:hover {
  background: #eef2ff;
  color: #4338ca;
}

.attendance-modal-empty {
  padding: 40px 24px;
  text-align: center;
  color: #64748b;
}

.attendance-modal-empty i {
  font-size: 2rem;
  color: #94a3b8;
  display: block;
  margin-bottom: 10px;
}

.attendance-modal-table-wrap {
  overflow: auto;
}

.attendance-modal-table {
  width: 100%;
  border-collapse: collapse;
}

.attendance-modal-table th,
.attendance-modal-table td {
  padding: 14px 18px;
  border-bottom: 1px solid #eef2f7;
  vertical-align: top;
  text-align: left;
}

.attendance-modal-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.attendance-modal-employee,
.attendance-modal-contact {
  display: grid;
  gap: 4px;
}

.attendance-modal-employee span,
.attendance-modal-contact span,
.attendance-modal-contact small {
  color: #64748b;
  font-size: 0.88rem;
}

.attendance-reminder-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: 500;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  background: #eef2ff;
  color: #4338ca;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;
}

.attendance-reminder-btn:hover:not(:disabled) {
  background: #c7d2fe;
  border-color: #818cf8;
}

.attendance-reminder-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.attendance-reminder-btn--sent {
  background: #dcfce7;
  color: #15803d;
  border-color: #bbf7d0;
}

.attendance-reminder-btn--all {
  padding: 7px 16px;
  font-size: 0.85rem;
  background: #4338ca;
  color: #fff;
  border-color: #4338ca;
}

.attendance-reminder-btn--all:hover:not(:disabled) {
  background: #3730a3;
  border-color: #3730a3;
}

.today-breakdown__dot--in { background: #0caf60; }
.today-breakdown__dot--out { background: #ff3a6e; }
.today-breakdown__dot--leave { background: #f1b44c; }

.dash-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--dash-gap);
  align-items: stretch;
}

.card--span-3 { grid-column: span 3; }
.card--span-4 { grid-column: span 4; }
.card--span-5 { grid-column: span 5; }
.card--span-6 { grid-column: span 6; }
.card--span-7 { grid-column: span 7; }
.card--span-8 { grid-column: span 8; }
.card--span-9 { grid-column: span 9; }
.card--span-12 { grid-column: 1 / -1; }

.attendance-chart-card,
.today-card {
  box-shadow: var(--dash-shadow);
}

.card-stack {
  display: flex;
  flex-direction: column;
  gap: var(--dash-gap);
  min-height: 100%;
}

.card-stack .card {
  flex: 1;
}

.dash__board .card,
.dash__board .metric-strip {
  opacity: 0;
  transform: translateY(12px);
}

.dash__board--ready .card,
.dash__board--ready .metric-strip {
  animation: dash-panel-in 0.45s ease forwards;
  animation-delay: calc(var(--panel-i, 0) * 60ms);
}

@keyframes dash-panel-in {
  to { opacity: 1; transform: translateY(0); }
}

.card {
  background: var(--dash-surface);
  border: 1px solid var(--dash-border);
  border-radius: var(--dash-radius);
  box-shadow: var(--dash-shadow);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--dash-border);
  background: #fafbfc;
}

.card__head--split {
  align-items: flex-start;
}

.card__head-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.card__hint {
  margin: 0.2rem 0 0;
  font-size: 0.75rem;
  color: var(--dash-muted);
  max-width: 42rem;
  line-height: 1.35;
}

.card__badge {
  font-size: 0.6875rem;
  font-weight: 650;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: var(--dash-muted);
}

.card__title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 650;
  color: var(--dash-text);
}

.card__action {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--dash-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
}

.card__action:hover {
  background: var(--dash-bg);
  color: var(--dash-accent);
}

.card__body {
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
}

.card__body--flush {
  padding: 0;
}

.card__body--today {
  gap: 0.75rem;
}

.card__chart {
  width: 100%;
  flex: 1;
  min-height: 200px;
}

.card__chart--lg { min-height: 300px; }
.card__chart--xl { min-height: 320px; width: 100%; }
.card__chart--md { min-height: 200px; }
.card__chart--sm { min-height: 170px; }

.turnover-hero__body {
  gap: 1rem;
}

.turnover-kpis--inline {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  border: none;
  border-radius: 10px;
  overflow: hidden;
}

.turnover-kpis--inline .turnover-kpis__item {
  border: 1px solid var(--dash-border);
  border-radius: 10px;
  border-right: 1px solid var(--dash-border);
}

.turnover-kpis__item--meta {
  background: #f8fafc;
}

.card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 160px;
  color: var(--dash-muted);
  font-size: 0.875rem;
}

.card__empty--inline {
  min-height: 3rem;
  flex-direction: row;
}

.card__empty i {
  font-size: 1.75rem;
  opacity: 0.4;
}

.card__legend {
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  border-top: 1px solid var(--dash-border);
  padding-top: 0.65rem;
}

.card__legend li {
  display: flex;
  justify-content: space-between;
  font-size: 0.8125rem;
  color: var(--dash-muted);
  padding: 0.2rem 0;
}

.card__legend strong {
  color: var(--dash-text);
  font-weight: 650;
  font-variant-numeric: tabular-nums;
}

.dash__loader {
  width: 2rem;
  height: 2rem;
  border: 2px solid #e2e8f0;
  border-top-color: var(--dash-accent);
  border-radius: 50%;
  animation: dash-spin 0.75s linear infinite;
}

.dash__loader--sm {
  width: 1.25rem;
  height: 1.25rem;
}

/* Today sidebar metrics */
.today-metrics {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.today-metrics__item {
  text-align: center;
  padding: 0.5rem 0.35rem;
  border-radius: 8px;
  border: 1px solid var(--dash-border);
  background: #f8fafc;
}

.today-metrics__item--ok { border-color: #bbf7d0; background: #f0fdf4; }
.today-metrics__item--bad { border-color: #fecaca; background: #fef2f2; }
.today-metrics__item--warn { border-color: #fde68a; background: #fffbeb; }

.today-metrics__label {
  display: block;
  font-size: 0.625rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--dash-muted);
}

.today-metrics__value {
  display: block;
  margin-top: 0.2rem;
  font-size: 1.125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Org metric strip */
.metric-strip {
  display: flex;
  align-items: stretch;
  background: var(--dash-surface);
  border: 1px solid var(--dash-border);
  border-radius: var(--dash-radius);
  box-shadow: var(--dash-shadow);
  overflow: hidden;
}

.metric-strip__item {
  flex: 1;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.metric-strip__item--primary {
  background: linear-gradient(135deg, #f0fdfa 0%, #fff 100%);
}

.metric-strip__divider {
  width: 1px;
  background: var(--dash-border);
  flex-shrink: 0;
}

.metric-strip__label {
  font-size: 0.6875rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--dash-muted);
}

.metric-strip__value {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  color: var(--dash-text);
  line-height: 1.1;
}

.metric-strip__value em {
  font-size: 0.65em;
  font-style: normal;
  font-weight: 650;
  color: var(--dash-muted);
  margin-left: 0.1em;
}

.metric-strip__value--plain {
  font-size: 1.35rem;
}

/* Turnover table */
.turnover-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-bottom: 1px solid var(--dash-border);
}

.turnover-kpis__item {
  padding: 0.85rem 1rem;
  text-align: center;
  border-right: 1px solid var(--dash-border);
}

.turnover-kpis__item:last-child {
  border-right: none;
}

.turnover-kpis__item--hire { background: #f0fdf4; }
.turnover-kpis__item--exit { background: #fef2f2; }
.turnover-kpis__item--net-up { background: #ecfdf5; }
.turnover-kpis__item--net-down { background: #fff7ed; }

.turnover-kpis__label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--dash-muted);
}

.turnover-kpis__value {
  display: block;
  margin-top: 0.25rem;
  font-size: 1.375rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.table-wrap {
  max-height: min(360px, 45vh);
  overflow: auto;
}

.table-wrap--tall {
  max-height: min(420px, 50vh);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8fafc;
}

.data-table th {
  text-align: left;
  padding: 0.65rem 1rem;
  font-size: 0.6875rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--dash-muted);
  border-bottom: 1px solid var(--dash-border);
}

.data-table td {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.data-table tbody tr:hover {
  background: #f8fafc;
}

.data-table--zebra tbody tr:nth-child(even) {
  background: #fafbfc;
}

.data-table__row--idle {
  opacity: 0.72;
}

.data-table__row--idle .data-table__chip {
  background: #f1f5f9;
  color: #94a3b8;
}

.data-table__num {
  text-align: right;
  width: 5rem;
}

.data-table__dept {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.data-table__avatar {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 6px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.625rem;
  font-weight: 700;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.data-table__name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

.data-table__chip {
  display: inline-block;
  min-width: 1.75rem;
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.data-table__chip--hire {
  background: #dcfce7;
  color: #15803d;
}

.data-table__chip--exit {
  background: #fee2e2;
  color: #b91c1c;
}

.data-table__net {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.data-table__net--up { color: #047857; }
.data-table__net--down { color: #b91c1c; }

.dash__footer {
  margin-top: 1.5rem;
  padding: 0.85rem 0;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: var(--dash-muted);
  border-top: 1px solid var(--dash-border);
}

#AttendanceContainer,
#TodayAttendanceContainer,
#DailyAttendanceContainer,
#TurnoverDeptChart,
#ExitChart,
#EntryChart,
#GenderContainer,
#disabilityChart {
  width: 100%;
}

.dash :deep(.highcharts-credits),
.dash :deep(a[href*="highcharts.com"]) {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

@media (max-width: 1280px) {
  .dash__kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .card--span-3,
  .card--span-4,
  .card--span-5,
  .card--span-6,
  .card--span-7,
  .card--span-8,
  .card--span-9 {
    grid-column: span 12;
  }
  .attendance-overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .turnover-kpis--inline {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .card-stack {
    flex-direction: row;
  }
  .card-stack .card {
    flex: 1;
  }
}

@media (max-width: 1024px) {
  .dash__main,
  .dash--nav-collapsed .dash__main {
    margin-left: 70px;
  }
  .metric-strip {
    flex-wrap: wrap;
  }
  .metric-strip__divider {
    display: none;
  }
  .metric-strip__item {
    flex: 1 1 40%;
    border-bottom: 1px solid var(--dash-border);
  }
  .card-stack {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .dash__main,
  .dash--nav-collapsed .dash__main {
    margin-left: 0;
    padding: 1rem;
  }
  .dash__kpis {
    grid-template-columns: 1fr;
  }
  .card--span-3,
  .card--span-4,
  .card--span-5,
  .card--span-6,
  .card--span-7,
  .card--span-8,
  .card--span-9,
  .card--span-12 {
    grid-column: span 12;
  }
  .attendance-overview {
    grid-template-columns: 1fr;
  }
  .attendance-toolbar {
    width: 100%;
    justify-content: flex-start;
  }
  .turnover-kpis--inline {
    grid-template-columns: 1fr;
  }
  .turnover-kpis {
    grid-template-columns: 1fr;
  }
  .turnover-kpis__item {
    border-right: none;
    border-bottom: 1px solid var(--dash-border);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dash__kpis .kpi,
  .dash__board .card,
  .dash__board .metric-strip {
    opacity: 1;
    transform: none;
    animation: none;
  }
}

@media print {
  .dash__toolbar-end,
  .card__action {
    display: none !important;
  }
  .dash__main {
    margin-left: 0 !important;
  }
}
</style>
